import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  MonitorPlay,
  Wifi,
  WifiOff,
  Plus,
  RefreshCw,
  Send,
  BarChart3,
  DollarSign,
  Eye,
  MousePointer,
  Target,
  Building2,
  MapPin,
  CheckCircle2,
  Clock,
  AlertCircle,
} from 'lucide-react';

export default function DV360Page() {
  const { user, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('inventory');
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [inventory, setInventory] = useState({ dv360_inventory: [], local_screens: [] });
  const [campaigns, setCampaigns] = useState([]);
  const [requests, setRequests] = useState([]);
  const [reporting, setReporting] = useState(null);
  const [content, setContent] = useState([]);
  
  // Dialogs
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  
  // Form states
  const [selectedInventory, setSelectedInventory] = useState([]);
  const [campaignForm, setCampaignForm] = useState({
    display_name: '',
    goal_type: 'CAMPAIGN_GOAL_TYPE_BRAND_AWARENESS',
    budget_micros: 10000000,
    start_date: '',
    end_date: '',
  });
  const [requestForm, setRequestForm] = useState({
    campaign_name: '',
    content_id: '',
    budget_micros: 10000000,
    bid_amount_micros: 100000,
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statusRes, inventoryRes, campaignsRes, requestsRes, reportingRes, contentRes] = await Promise.all([
        api.get('/dv360/connection-status'),
        api.get('/dv360/inventory'),
        api.get('/dv360/campaigns'),
        api.get('/dv360/inventory-requests'),
        api.get('/dv360/reporting'),
        api.get('/content?approval_status=approved'),
      ]);
      
      setConnectionStatus(statusRes.data);
      setInventory(inventoryRes.data);
      setCampaigns(campaignsRes.data.campaigns || []);
      setRequests(requestsRes.data.requests || []);
      setReporting(reportingRes.data);
      setContent(contentRes.data.filter(c => c.approval_status === 'approved'));
    } catch (error) {
      console.error('Error loading DV360 data:', error);
      toast.error('Failed to load DV360 data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    try {
      const response = await api.post('/dv360/campaigns', {
        ...campaignForm,
        advertiser_id: connectionStatus?.advertiser_id,
      });
      
      if (response.data.success) {
        toast.success('Campaign created successfully');
        setShowCampaignDialog(false);
        setCampaignForm({
          display_name: '',
          goal_type: 'CAMPAIGN_GOAL_TYPE_BRAND_AWARENESS',
          budget_micros: 10000000,
          start_date: '',
          end_date: '',
        });
        loadData();
      }
    } catch (error) {
      toast.error('Failed to create campaign');
    }
  };

  const handleSubmitRequest = async () => {
    if (selectedInventory.length === 0) {
      toast.error('Please select at least one screen');
      return;
    }
    if (!requestForm.content_id) {
      toast.error('Please select content to display');
      return;
    }
    
    try {
      const response = await api.post('/dv360/inventory-request', {
        ...requestForm,
        screen_ids: selectedInventory,
      });
      
      if (response.data.success) {
        toast.success(`Request submitted for ${selectedInventory.length} screens`);
        setShowRequestDialog(false);
        setSelectedInventory([]);
        setRequestForm({
          campaign_name: '',
          content_id: '',
          budget_micros: 10000000,
          bid_amount_micros: 100000,
          start_date: '',
          end_date: '',
        });
        loadData();
      }
    } catch (error) {
      toast.error('Failed to submit request');
    }
  };

  const toggleInventorySelection = (id) => {
    setSelectedInventory(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const formatMicros = (micros) => {
    return `$${(micros / 1000000).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dv360-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            DV360 Integration
          </h1>
          <p className="text-zinc-400 mt-1">
            Connect to Google Display & Video 360 inventory
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={loadData}
            className="border-zinc-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={() => setShowRequestDialog(true)}
            className="bg-blue-600 hover:bg-blue-500"
            disabled={selectedInventory.length === 0}
          >
            <Send className="w-4 h-4 mr-2" />
            Send Request ({selectedInventory.length})
          </Button>
        </div>
      </div>

      {/* Connection Status */}
      <Card className="glass-card border-zinc-800">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl ${connectionStatus?.connected ? 'bg-emerald-500/10' : 'bg-rose-500/10'}`}>
                {connectionStatus?.connected ? (
                  <Wifi className="w-6 h-6 text-emerald-400" />
                ) : (
                  <WifiOff className="w-6 h-6 text-rose-400" />
                )}
              </div>
              <div>
                <h3 className="text-white font-semibold">
                  {connectionStatus?.connected ? 'Connected to DV360' : 'DV360 Connection'}
                </h3>
                <p className="text-zinc-400 text-sm">
                  Partner ID: {connectionStatus?.partner_id || 'Not configured'} | 
                  Advertiser ID: {connectionStatus?.advertiser_id || 'Not configured'}
                </p>
                {connectionStatus?.auth_method && (
                  <p className="text-zinc-500 text-xs mt-1">
                    Auth: {connectionStatus.auth_method}
                    {connectionStatus.service_account_email && ` (${connectionStatus.service_account_email})`}
                  </p>
                )}
              </div>
            </div>
            <Badge 
              variant="outline" 
              className={connectionStatus?.connected 
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
              }
            >
              {connectionStatus?.connected ? 'Active' : (inventory.demo_mode ? 'Demo Mode' : 'Needs Setup')}
            </Badge>
          </div>
          
          {/* Permission Error Message */}
          {connectionStatus?.suggestion && (
            <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                <div>
                  <p className="text-amber-400 font-medium">Permission Required</p>
                  <p className="text-amber-300/80 text-sm mt-1">
                    {connectionStatus.error}
                  </p>
                  <p className="text-zinc-400 text-sm mt-2">
                    <strong>To fix:</strong> {connectionStatus.suggestion}
                  </p>
                  {connectionStatus.service_account_email && (
                    <div className="mt-2 p-2 bg-zinc-900 rounded font-mono text-xs text-zinc-300">
                      {connectionStatus.service_account_email}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Cards */}
      {reporting && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="glass-card border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Target className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Campaigns</p>
                  <p className="text-white text-2xl font-bold">{reporting.summary.total_campaigns}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <DollarSign className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Total Spend</p>
                  <p className="text-white text-2xl font-bold">{formatMicros(reporting.summary.total_spend_micros)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Eye className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Impressions</p>
                  <p className="text-white text-2xl font-bold">{reporting.summary.total_impressions.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <MousePointer className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">CTR</p>
                  <p className="text-white text-2xl font-bold">{reporting.summary.ctr}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="inventory" className="data-[state=active]:bg-blue-600">
            <MonitorPlay className="w-4 h-4 mr-2" />
            Inventory ({inventory.dv360_inventory?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="data-[state=active]:bg-blue-600">
            <Target className="w-4 h-4 mr-2" />
            Campaigns ({campaigns.length})
          </TabsTrigger>
          <TabsTrigger value="requests" className="data-[state=active]:bg-blue-600">
            <Send className="w-4 h-4 mr-2" />
            Requests ({requests.length})
          </TabsTrigger>
          <TabsTrigger value="reporting" className="data-[state=active]:bg-blue-600">
            <BarChart3 className="w-4 h-4 mr-2" />
            Reporting
          </TabsTrigger>
        </TabsList>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="mt-4">
          <Card className="glass-card border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-white">
                Available DV360 Inventory
              </CardTitle>
              {inventory.demo_mode && (
                <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/20">
                  Demo Data
                </Badge>
              )}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="w-12"></TableHead>
                    <TableHead className="text-zinc-500">Screen/Network</TableHead>
                    <TableHead className="text-zinc-500">Exchange</TableHead>
                    <TableHead className="text-zinc-500">Location</TableHead>
                    <TableHead className="text-zinc-500">CPM Rate</TableHead>
                    <TableHead className="text-zinc-500">Impressions</TableHead>
                    <TableHead className="text-zinc-500">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inventory.dv360_inventory?.map((item) => (
                    <TableRow key={item.id} className="border-zinc-800">
                      <TableCell>
                        <Checkbox
                          checked={selectedInventory.includes(item.id)}
                          onCheckedChange={() => toggleInventorySelection(item.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
                            <MonitorPlay className="w-5 h-5 text-blue-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{item.name}</p>
                            <p className="text-zinc-500 text-sm">Deal: {item.deal_id}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-zinc-800 text-zinc-300 border-zinc-700">
                          {item.exchange}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-zinc-400">
                          <MapPin className="w-4 h-4" />
                          {item.location}
                        </div>
                      </TableCell>
                      <TableCell className="text-emerald-400 font-medium">
                        {formatMicros(item.rate_micros)}
                      </TableCell>
                      <TableCell className="text-zinc-300">
                        {item.impressions_available?.toLocaleString() || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={item.status === 'ACTIVE' 
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                            : 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
                          }
                        >
                          {item.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {selectedInventory.length > 0 && (
                <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <p className="text-blue-400">
                    <strong>{selectedInventory.length}</strong> screens selected. 
                    Click "Send Request" to submit your content for display.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="mt-4">
          <Card className="glass-card border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-white">
                DV360 Campaigns
              </CardTitle>
              <Button onClick={() => setShowCampaignDialog(true)} className="bg-blue-600 hover:bg-blue-500">
                <Plus className="w-4 h-4 mr-2" />
                New Campaign
              </Button>
            </CardHeader>
            <CardContent>
              {campaigns.length === 0 ? (
                <div className="text-center py-12">
                  <Target className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400">No campaigns yet. Create your first DV360 campaign.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-500">Campaign Name</TableHead>
                      <TableHead className="text-zinc-500">DV360 ID</TableHead>
                      <TableHead className="text-zinc-500">Budget</TableHead>
                      <TableHead className="text-zinc-500">Status</TableHead>
                      <TableHead className="text-zinc-500">Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {campaigns.map((campaign) => (
                      <TableRow key={campaign.id} className="border-zinc-800">
                        <TableCell className="text-white font-medium">{campaign.display_name}</TableCell>
                        <TableCell className="text-zinc-400 font-mono text-sm">
                          {campaign.dv360_campaign_id}
                        </TableCell>
                        <TableCell className="text-emerald-400">
                          {formatMicros(campaign.budget_micros || 0)}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={campaign.status === 'ACTIVE' 
                              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                              : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                            }
                          >
                            {campaign.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-zinc-400">
                          {new Date(campaign.created_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Requests Tab */}
        <TabsContent value="requests" className="mt-4">
          <Card className="glass-card border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-white">
                Inventory Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              {requests.length === 0 ? (
                <div className="text-center py-12">
                  <Send className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400">No requests yet. Select inventory and submit a request.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-500">Campaign</TableHead>
                      <TableHead className="text-zinc-500">Content</TableHead>
                      <TableHead className="text-zinc-500">Screens</TableHead>
                      <TableHead className="text-zinc-500">Budget</TableHead>
                      <TableHead className="text-zinc-500">Status</TableHead>
                      <TableHead className="text-zinc-500">Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((request) => (
                      <TableRow key={request.id} className="border-zinc-800">
                        <TableCell className="text-white font-medium">{request.campaign_name}</TableCell>
                        <TableCell className="text-zinc-400">{request.content_name}</TableCell>
                        <TableCell className="text-zinc-300">{request.screen_ids?.length || 0} screens</TableCell>
                        <TableCell className="text-emerald-400">{formatMicros(request.budget_micros)}</TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={
                              request.status === 'APPROVED' 
                                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                : request.status === 'PENDING'
                                ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                : 'bg-rose-500/10 text-rose-400 border-rose-500/20'
                            }
                          >
                            {request.status === 'APPROVED' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                            {request.status === 'PENDING' && <Clock className="w-3 h-3 mr-1" />}
                            {request.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-zinc-400">
                          {new Date(request.created_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reporting Tab */}
        <TabsContent value="reporting" className="mt-4">
          <Card className="glass-card border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-400" />
                Performance Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reporting && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-zinc-800/50 rounded-lg">
                    <p className="text-zinc-400 text-sm">Total Budget</p>
                    <p className="text-white text-xl font-bold">{formatMicros(reporting.summary.total_budget_micros)}</p>
                  </div>
                  <div className="p-4 bg-zinc-800/50 rounded-lg">
                    <p className="text-zinc-400 text-sm">Total Spend</p>
                    <p className="text-emerald-400 text-xl font-bold">{formatMicros(reporting.summary.total_spend_micros)}</p>
                  </div>
                  <div className="p-4 bg-zinc-800/50 rounded-lg">
                    <p className="text-zinc-400 text-sm">Avg CPM</p>
                    <p className="text-white text-xl font-bold">{formatMicros(reporting.summary.avg_cpm_micros)}</p>
                  </div>
                  <div className="p-4 bg-zinc-800/50 rounded-lg">
                    <p className="text-zinc-400 text-sm">Active Campaigns</p>
                    <p className="text-blue-400 text-xl font-bold">{reporting.summary.active_campaigns}</p>
                  </div>
                </div>
              )}
              
              {reporting?.demo_mode && (
                <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-400" />
                    <p className="text-amber-400">
                      Showing demo data. Connect with valid DV360 credentials for live reporting.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Campaign Dialog */}
      <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Space Grotesk' }}>
              Create DV360 Campaign
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300">Campaign Name</Label>
              <Input
                value={campaignForm.display_name}
                onChange={(e) => setCampaignForm({ ...campaignForm, display_name: e.target.value })}
                placeholder="Summer Billboard Campaign"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Goal Type</Label>
              <Select
                value={campaignForm.goal_type}
                onValueChange={(value) => setCampaignForm({ ...campaignForm, goal_type: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="CAMPAIGN_GOAL_TYPE_BRAND_AWARENESS">Brand Awareness</SelectItem>
                  <SelectItem value="CAMPAIGN_GOAL_TYPE_DRIVE_OFFLINE_ACTION">Drive Offline Action</SelectItem>
                  <SelectItem value="CAMPAIGN_GOAL_TYPE_PROMOTE_PRODUCTS">Promote Products</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300">Budget ($)</Label>
              <Input
                type="number"
                value={campaignForm.budget_micros / 1000000}
                onChange={(e) => setCampaignForm({ ...campaignForm, budget_micros: parseFloat(e.target.value) * 1000000 })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Start Date</Label>
                <Input
                  type="date"
                  value={campaignForm.start_date}
                  onChange={(e) => setCampaignForm({ ...campaignForm, start_date: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-300">End Date</Label>
                <Input
                  type="date"
                  value={campaignForm.end_date}
                  onChange={(e) => setCampaignForm({ ...campaignForm, end_date: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowCampaignDialog(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleCreateCampaign} className="bg-blue-600 hover:bg-blue-500">
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Request Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Space Grotesk' }}>
              Send Inventory Request
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-blue-400 text-sm">
                Selected {selectedInventory.length} screen(s) for this request
              </p>
            </div>
            <div>
              <Label className="text-zinc-300">Campaign Name</Label>
              <Input
                value={requestForm.campaign_name}
                onChange={(e) => setRequestForm({ ...requestForm, campaign_name: e.target.value })}
                placeholder="My Billboard Campaign"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Content to Display</Label>
              <Select
                value={requestForm.content_id}
                onValueChange={(value) => setRequestForm({ ...requestForm, content_id: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue placeholder="Select content" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {content.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Budget ($)</Label>
                <Input
                  type="number"
                  value={requestForm.budget_micros / 1000000}
                  onChange={(e) => setRequestForm({ ...requestForm, budget_micros: parseFloat(e.target.value) * 1000000 })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Bid CPM ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={requestForm.bid_amount_micros / 1000000}
                  onChange={(e) => setRequestForm({ ...requestForm, bid_amount_micros: parseFloat(e.target.value) * 1000000 })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Start Date</Label>
                <Input
                  type="date"
                  value={requestForm.start_date}
                  onChange={(e) => setRequestForm({ ...requestForm, start_date: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-300">End Date</Label>
                <Input
                  type="date"
                  value={requestForm.end_date}
                  onChange={(e) => setRequestForm({ ...requestForm, end_date: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowRequestDialog(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleSubmitRequest} className="bg-blue-600 hover:bg-blue-500">
              <Send className="w-4 h-4 mr-2" />
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
